/**
 * chunkRecovery.ts — recovery dai fallimenti di caricamento dei chunk lazy
 *
 * PROBLEMA (Set 2026): il Service Worker può servire un index.html stantio
 * che referenzia chunk con hash vecchi (es. CerchiaPage-hCi0m9Li.js) non
 * più presenti sul CDN → `Failed to fetch dynamically imported module`
 * → ErrorBoundary su pagine lazy (Cerchia, Chat, ecc.).
 *
 * FIX: al primo errore di questo tipo si UNREGISTRA il Service Worker,
 * si svuotano tutte le cache e si ricarica la pagina UNA volta
 * (guard in sessionStorage): il reload scarica index + chunk freschi.
 * Su Capacitor (hostname localhost) il SW non esiste: no-op.
 *
 * Le dipendenze sono iniettabili (parametro deps) per renderla testabile
 * senza mock globali di window.location.
 */

const SESSION_KEY = "rc_chunk_recovery_done";

export interface ChunkRecoveryDeps {
  hostname?: string;
  isLocalhost?: boolean;
  session?: Pick<Storage, "getItem" | "setItem"> | null;
  getRegistrations?: () => Promise<
    ReadonlyArray<{ unregister: () => Promise<boolean> }>
  >;
  cachesApi?: {
    keys: () => Promise<string[]>;
    delete: (key: string) => Promise<boolean>;
  } | null;
  reload?: () => void;
}

function defaultDeps(): Required<ChunkRecoveryDeps> {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "localhost";
  return {
    hostname,
    isLocalhost: hostname === "localhost",
    session: typeof sessionStorage !== "undefined" ? sessionStorage : null,
    getRegistrations: () =>
      "serviceWorker" in navigator
        ? navigator.serviceWorker.getRegistrations()
        : Promise.resolve([]),
    cachesApi:
      typeof caches !== "undefined"
        ? {
            keys: () => caches.keys(),
            delete: (key: string) => caches.delete(key),
          }
        : null,
    reload: () => window.location.reload(),
  };
}

export function recoverFromChunkFailure(
  deps: ChunkRecoveryDeps = {},
): void {
  const d: Required<ChunkRecoveryDeps> = { ...defaultDeps(), ...deps };
  if (d.isLocalhost) return;
  const session = d.session;
  if (!session) return;
  // Una sola ricarica per sessione: evita loop infiniti
  if (session.getItem(SESSION_KEY)) return;
  session.setItem(SESSION_KEY, "1");

  void (async () => {
    try {
      const registrations = await d.getRegistrations();
      for (const reg of registrations) {
        try {
          await reg.unregister();
        } catch {
          /* best effort */
        }
      }
      if (d.cachesApi) {
        const keys = await d.cachesApi.keys();
        await Promise.all(
          keys.map((k) => d.cachesApi!.delete(k).catch(() => false)),
        );
      }
    } catch {
      /* best effort: il reload avviene comunque */
    }
    d.reload();
  })();
}

/** Installa i listener globali (chiamato una volta da main.tsx). */
export function installChunkRecovery(): void {
  if (typeof window === "undefined") return;

  // Evento Vite: fallimento del preload di un chunk dinamico
  window.addEventListener("vite:preloadError", (event: Event) => {
    event?.preventDefault?.();
    recoverFromChunkFailure();
  });

  // Rete di sicurezza: reject non gestite da import() dinamici
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event?.reason;
      const message = String(reason?.message ?? reason ?? "");
      if (/Failed to fetch dynamically imported module/i.test(message)) {
        recoverFromChunkFailure();
      }
    },
  );
}
